/* MARCA | 24 Sep 2026 | Cart drawer multi-case offer. The product already in the cart is the first case. Add sends every extra case in one request. */
if (!customElements.get('cart-purchase-options')) {
  class CartPurchaseOptions extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.adding = false;
      this.forceRefresh = false;
      this.refreshing = false;
      this.pendingRefresh = false;
      this.pendingForce = false;

      this.onClick = (event) => this.handleClick(event);
      this.onChange = (event) => this.handleChange(event);
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeMenus();
      };
      /* MARCA | 24 Sep 2026 | Line items refresh on their own. This refetches the offer when the cart is no longer a single product. */
      this.onCartChange = () => {
        const force = this.forceRefresh;
        this.forceRefresh = false;
        this.refreshFromCart(force);
      };

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('theme:cart:change', this.onCartChange);

      this.activate();
    }

    disconnectedCallback() {
      this.dataset.bound = 'false';
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('theme:cart:change', this.onCartChange);
    }

    activate() {
      this.unitPrice = Number(this.dataset.unitPrice) || 0;
      this.cansPerCase = Number(this.dataset.cans) || 12;
      this.fillPickers();
      if (this.querySelector('[data-purchase-option]')) this.updatePrices();
      this.syncAddLock();
    }

    fillPickers() {
      const template = this.querySelector('[data-picker-template]');
      if (!template) return;

      this.querySelectorAll('[data-menu]').forEach((menu) => {
        menu.replaceChildren(template.content.cloneNode(true));
      });
    }

    selectedOption() {
      return this.querySelector('.purchase-options__option.is-selected');
    }

    handleChange(event) {
      const input = event.target.closest('[data-purchase-option]');
      if (!input) return;

      this.querySelectorAll('.purchase-options__option').forEach((option) => {
        const optionInput = option.querySelector('[data-purchase-option]');
        option.classList.toggle('is-selected', Boolean(optionInput?.checked));
      });

      this.closeMenus();
      this.updatePrices();
      this.syncAddLock();
    }

    handleClick(event) {
      /* MARCA | 24 Sep 2026 | A selected case row can be clicked again to clear it. Radios do not uncheck on their own. */
      const label = event.target.closest('.purchase-options__label');
      if (label?.closest('.purchase-options__option')?.classList.contains('is-selected')) {
        event.preventDefault();
        this.clearSelection();
        return;
      }

      const addButton = event.target.closest('[data-add]');
      if (addButton) {
        event.preventDefault();
        this.addCases(addButton);
        return;
      }

      const choice = event.target.closest('[data-choice]');
      if (choice) {
        event.preventDefault();
        this.selectChoice(choice);
        return;
      }

      const opener = event.target.closest('[data-open-picker]');
      if (opener) {
        event.preventDefault();
        const slot = opener.closest('[data-slot]');
        if (slot) this.openPicker(slot);
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') this.closeMenus();

      const input = event.target.closest('[data-purchase-option]');
      if (!input || (event.key !== 'Enter' && event.key !== ' ')) return;
      if (!input.closest('.purchase-options__option')?.classList.contains('is-selected')) return;

      event.preventDefault();
      this.clearSelection();
    }

    clearSelection() {
      this.querySelectorAll('[data-purchase-option]').forEach((input) => {
        input.checked = false;
      });
      this.querySelectorAll('.purchase-options__option.is-selected').forEach((option) => {
        option.classList.remove('is-selected');
      });
      this.closeMenus();
      this.updatePrices();
      this.syncAddLock();
    }

    openPicker(slot) {
      const menu = slot.querySelector('[data-menu]');
      if (!menu) return;

      const willOpen = menu.hidden;
      this.closeMenus();
      if (!willOpen) return;

      menu.hidden = false;
      slot.classList.add('is-open');
      slot.querySelectorAll('[aria-expanded]').forEach((node) => node.setAttribute('aria-expanded', 'true'));
    }

    closeMenus() {
      this.querySelectorAll('[data-menu]').forEach((menu) => {
        menu.hidden = true;
      });
      this.querySelectorAll('[data-slot]').forEach((slot) => {
        slot.classList.remove('is-open');
        slot.querySelectorAll('[aria-expanded]').forEach((node) => node.setAttribute('aria-expanded', 'false'));
      });
    }

    selectChoice(choice) {
      if (choice.disabled) return;

      const slot = choice.closest('[data-slot]');
      const card = choice.querySelector('.purchase-options__case');
      const body = slot?.querySelector('[data-slot-body]');
      if (!slot || !card || !body) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'purchase-options__selected';
      button.dataset.openPicker = '';
      button.setAttribute('aria-expanded', 'false');
      button.appendChild(card.cloneNode(true));

      body.replaceChildren(button);
      slot.dataset.selectedVariant = choice.dataset.variantId;
      slot.dataset.selectedPrice = choice.dataset.price;
      this.closeMenus();
      this.updatePrices();
      this.syncAddLock();
    }

    isComplete(option) {
      const slots = [...option.querySelectorAll('[data-slot]')];
      return slots.length > 0 && slots.every((slot) => slot.dataset.selectedVariant);
    }

    updatePrices() {
      this.querySelectorAll('.purchase-options__option').forEach((option) => {
        this.renderSummary(option);
      });
    }

    renderSummary(option) {
      const slots = [...option.querySelectorAll('[data-slot]')];
      const filled = slots.filter((slot) => slot.dataset.selectedVariant);
      const count = 1 + filled.length;
      const original = this.unitPrice + filled.reduce((sum, slot) => sum + Number(slot.dataset.selectedPrice || 0), 0);
      const discount = Number(option.dataset.discount) || 0;
      const discounted = this.discounted(original, discount);

      const cans = option.querySelector('[data-summary-cans]');
      const cases = option.querySelector('[data-summary-cases]');
      const total = option.querySelector('[data-summary-total]');
      const compare = option.querySelector('[data-summary-compare]');
      const subtotal = option.querySelector('[data-summary-subtotal]');
      const badge = option.querySelector('[data-summary-off]');

      if (cans) cans.textContent = `(${this.cansPerCase * count} cans)`;
      if (cases) cases.textContent = count === 1 ? '1 CASE' : `${count} CASES`;
      if (subtotal) subtotal.textContent = this.money(discounted);

      const showDiscount = discount > 0 && discounted !== original;
      if (badge) {
        badge.hidden = !showDiscount;
        badge.textContent = `${discount}% OFF`;
      }
      if (compare) {
        compare.hidden = !showDiscount;
        compare.textContent = this.money(original);
      }
      if (total) {
        total.hidden = showDiscount;
        total.textContent = this.money(original);
      }
    }

    discounted(cents, percent) {
      const rate = Math.min(100, Math.max(0, Number(percent) || 0));
      return Math.round((Number(cents) * (100 - rate)) / 100);
    }

    money(cents) {
      const value = Math.round(Number(cents) || 0);
      const format = window.theme?.moneyWithoutCurrencyFormat || window.theme?.moneyFormat;

      if (window.theme?.formatMoney) return window.theme.formatMoney(value, format);
      return (value / 100).toFixed(2);
    }

    addCases(button) {
      if (this.adding) return;

      const option = this.selectedOption();
      if (!option || !this.isComplete(option)) {
        this.syncAddLock();
        const emptySlot = [...(option?.querySelectorAll('[data-slot]') || [])].find((slot) => !slot.dataset.selectedVariant);
        if (emptySlot) this.openPicker(emptySlot);
        return;
      }

      const items = this.buildItems();
      if (!items.length) return;

      this.adding = true;
      this.clearError();
      this.setButtonLoading(true);

      const addUrl = window.theme.routes.cart_add_url.endsWith('.js')
        ? window.theme.routes.cart_add_url
        : `${window.theme.routes.cart_add_url}.js`;

      fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data?.status) {
            this.failAdd(data);
            return;
          }

          if (window.theme.settings?.cartType === 'page') {
            window.location = window.theme.routes.cart_url;
            return;
          }

          this.forceRefresh = true;
          document.dispatchEvent(
            new CustomEvent('theme:product:add', {
              bubbles: true,
              detail: {
                response: data,
                button,
              },
            })
          );
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        })
        .catch((error) => {
          this.failAdd({
            message: error?.message || 'Could not add these cases to the cart.',
          });
        })
        .finally(() => {
          this.adding = false;
          this.setButtonLoading(false);
          this.syncAddLock();
        });
    }

    /* MARCA | 25 Sep 2026 | The cart add stays locked until every extra case is chosen. */
    syncAddLock() {
      const option = this.selectedOption();
      const locked = Boolean(option) && !this.isComplete(option);

      this.querySelectorAll('[data-add]').forEach((button) => {
        button.disabled = locked;
        if (locked) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });

      if (locked) {
        this.warningVisible = true;
        this.showError(this.dataset.incompleteMessage || 'Select your cases, or choose 1 case.');
        return;
      }

      if (this.warningVisible) {
        this.warningVisible = false;
        this.clearError();
      }
    }

    /* MARCA | 24 Sep 2026 | Only the extra cases are posted. The open cart line is already the first case. */
    buildItems() {
      const option = this.selectedOption();
      const lines = new Map();

      option?.querySelectorAll('[data-slot]').forEach((slot) => {
        const id = Number(slot.dataset.selectedVariant);
        if (!id) return;
        const existing = lines.get(id) || { id, quantity: 0 };
        existing.quantity += 1;
        lines.set(id, existing);
      });

      return [...lines.values()];
    }

    async refreshFromCart(force) {
      if (this.refreshing) {
        this.pendingRefresh = true;
        this.pendingForce = this.pendingForce || force;
        return;
      }

      this.refreshing = true;

      try {
        const cartUrl = window.theme?.routes?.cart_url;
        if (!cartUrl) return;

        const cartResponse = await fetch(`${cartUrl}.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          credentials: 'same-origin',
        });
        if (!cartResponse.ok) return;

        const cart = await cartResponse.json();
        const count = cart.items?.length || 0;
        const variantId = count === 1 ? String(cart.items[0].variant_id) : '';
        const showing = !this.hidden && Boolean(this.querySelector('[data-purchase-option]'));
        const sameCart = String(count) === String(this.dataset.itemCount || '') && variantId === (this.dataset.variantId || '');

        if (!force && sameCart && (count !== 1 || showing)) return;

        const root = window.theme?.routes?.root || '/';
        const sectionUrl = new URL(root, window.location.origin);
        sectionUrl.searchParams.set('section_id', this.dataset.sectionId);

        const sectionResponse = await fetch(sectionUrl, {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (!sectionResponse.ok) return;

        const html = await sectionResponse.text();
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-purchase-options');
        if (!next) return;

        this.applyMarkup(next);
      } catch (error) {
        return error;
      } finally {
        this.refreshing = false;
        if (this.pendingRefresh) {
          const nextForce = this.pendingForce;
          this.pendingRefresh = false;
          this.pendingForce = false;
          this.refreshFromCart(nextForce);
        }
      }
    }

    applyMarkup(next) {
      [...this.attributes].forEach((attribute) => {
        if (!attribute.name.startsWith('data-')) return;
        if (attribute.name === 'data-bound' || attribute.name === 'data-section-id') return;
        this.removeAttribute(attribute.name);
      });

      [...next.attributes].forEach((attribute) => {
        if (attribute.name === 'data-bound' || attribute.name === 'data-section-id') return;
        if (attribute.name === 'class' || attribute.name.startsWith('data-')) {
          this.setAttribute(attribute.name, attribute.value);
        }
      });

      this.hidden = next.hasAttribute('hidden');
      this.innerHTML = next.innerHTML;
      this.activate();
    }

    setButtonLoading(isLoading) {
      this.querySelectorAll('[data-add]').forEach((button) => {
        button.classList.toggle('is-loading', isLoading);
        button.disabled = isLoading;
        button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      });
    }

    failAdd(data) {
      const message = data?.description && data.description !== data.message
        ? `${data.message}: ${data.description}`
        : data?.message || data?.description || 'Could not add these cases to the cart.';

      this.showError(message);
      this.setButtonLoading(false);
    }

    showError(message) {
      const error = this.querySelector('[data-error]');
      if (!error) return;
      error.hidden = false;
      error.textContent = message;
    }

    clearError() {
      const error = this.querySelector('[data-error]');
      if (!error) return;
      error.hidden = true;
      error.textContent = '';
    }
  }

  customElements.define('cart-purchase-options', CartPurchaseOptions);
}
